import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { pspKeys, pspService, type OpenPspFileBody } from '../../../../../services/pspService';
import patientRecordService, { type UpsertSpecialtyDrugBody } from '../../../../../services/patientRecordService';
import { patientRecordQueryKey } from '../../../../../hooks/queries/usePatientRecord';
import { apiErrorMessage } from '../../../../../utils/apiErrorMessage';
import { getPharmacyTodayString } from '../../../../../utils/timeUtils';

/** One selected prescription row, reduced to what the dialog needs. */
export interface MarkSpecialtyRx {
  rxNumber: string;
  drugName: string;
}

export interface MarkSpecialtyDialogProps {
  open: boolean;
  /** The pharmacy Patient 360's record read is keyed on — the rows are posted
   * here, and the record read is refreshed under exactly this key. */
  pharmacyId: string;
  patientId: string;
  /** The selected rows; one POST per postable entry, in this order. */
  prescriptions: MarkSpecialtyRx[];
  /**
   * PRX-129: the PSP workflow runs at the patient's pharmacy (Fillware). Then
   * each Rx opens a PSP file; otherwise the server refuses the PSP routes and
   * the drug is added through the PRX-80 relations POST, outside the workflow.
   * `undefined` while the patient's pharmacy system is loading or unknown:
   * nothing can be sent then, so neither path is guessed.
   */
  pspEnabled: boolean | undefined;
  /** From `usePatient360ReadOnly()` — the whole dialog is disabled and nothing is sent. */
  readOnly: boolean;
  readOnlyMessage?: string | null;
  onClose: () => void;
  /**
   * Called once EVERY postable row succeeded, with the Rx numbers this dialog
   * refused for having no drug name (empty when it refused none). The caller
   * keeps exactly those rows selected, so the reason they were not marked is
   * still on screen after the dialog closes. The record read is refreshed by
   * the dialog itself, not here — see `handleSave`.
   */
  onMarked: (skippedRxNumbers: string[]) => void;
}

/** Shown while the patient record's pharmacy system is loading or unknown (PRX-129: no fallback). */
export const PMS_UNKNOWN_MESSAGE =
  "Checking this patient's pharmacy system. Marking is available once it is known.";

/** Why a row is never posted — the dialog's "skipped" note and the table's "not marked" note say the same words. */
export const NO_DRUG_NAME_REASON = 'no drug name';

const rxList = (rows: readonly MarkSpecialtyRx[]): string => rows.map((rx) => `Rx ${rx.rxNumber}`).join(', ');

/**
 * "Mark as specialty" (PRX-80 Task 13; PRX-129). Opens one PSP file per
 * selected Rx through the PSP open-file endpoint, with the Rx number, the
 * drug name from the row and the received date. Every file starts at
 * "Request received": there is no stage select (the legacy stage column is
 * not the workflow, ADR-117).
 *
 * The POSTs run sequentially and stop at the first failure: the server's
 * sentence is shown verbatim (an Rx with an open file already is a 409), the
 * failed Rx is named by number only, and the dialog stays open so nothing is
 * silently half-done. The dialog remembers which rows already went through
 * and never re-sends them on a retry. A row with no drug name would fail the
 * server's validation and abort the batch halfway — it is named and excluded
 * BEFORE anything is posted.
 *
 * The created rows are on the patient's record read (`specialtyDrugs`), so a
 * save that created at least one refreshes it — including a batch that then
 * stopped at a failure, which never reaches `onMarked`. The key is the ids
 * this save posted with, never re-derived.
 */
export const MarkSpecialtyDialog: React.FC<MarkSpecialtyDialogProps> = ({
  open,
  pharmacyId,
  patientId,
  prescriptions,
  pspEnabled,
  readOnly,
  readOnlyMessage,
  onClose,
  onMarked,
}) => {
  const queryClient = useQueryClient();
  const [receivedDate, setReceivedDate] = useState<string>(getPharmacyTodayString());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Rx numbers this dialog has already added — never re-sent. */
  const [alreadyAdded, setAlreadyAdded] = useState<ReadonlySet<string>>(new Set());

  const skipped = useMemo(() => prescriptions.filter((rx) => !rx.drugName.trim()), [prescriptions]);
  const added = useMemo(() => prescriptions.filter((rx) => alreadyAdded.has(rx.rxNumber)), [prescriptions, alreadyAdded]);
  const postable = useMemo(
    () => prescriptions.filter((rx) => rx.drugName.trim() && !alreadyAdded.has(rx.rxNumber)),
    [prescriptions, alreadyAdded],
  );

  const reset = () => {
    setReceivedDate(getPharmacyTodayString());
    setNotes('');
    setError(null);
    setAlreadyAdded(new Set());
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (readOnly || saving || postable.length === 0 || !receivedDate || pspEnabled === undefined) return;
    setSaving(true);
    setError(null);
    const done = new Set(alreadyAdded);
    let created = 0;
    try {
      for (const rx of postable) {
        try {
          if (pspEnabled) {
            const body: OpenPspFileBody = { rxNumber: rx.rxNumber, drugName: rx.drugName.trim(), receivedDate };
            if (notes.trim()) body.note = notes.trim();
            await pspService.openFile(pharmacyId, patientId, body);
          } else {
            const body: UpsertSpecialtyDrugBody = { drugName: rx.drugName.trim(), receivedDate };
            if (notes.trim()) body.notes = notes.trim();
            await patientRecordService.createSpecialtyDrug(pharmacyId, patientId, body);
          }
          created += 1;
          done.add(rx.rxNumber);
          setAlreadyAdded(new Set(done));
        } catch (e) {
          setError(`Rx ${rx.rxNumber}: ${apiErrorMessage(e, 'Could not mark this prescription as specialty.')}`);
          return;
        }
      }
      reset();
      onMarked(skipped.map((rx) => rx.rxNumber));
    } finally {
      setSaving(false);
      if (created > 0) {
        queryClient.invalidateQueries({ queryKey: patientRecordQueryKey(pharmacyId, patientId) });
        queryClient.invalidateQueries({ queryKey: pspKeys.all });
      }
    }
  };

  const disabled = readOnly || saving;
  const title = readOnly ? readOnlyMessage ?? undefined : undefined;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="mark-specialty-title">
      <DialogTitle id="mark-specialty-title">Mark as specialty</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {postable.length === 1
            ? `One prescription (Rx ${postable[0].rxNumber}) will be added to this patient's specialty drugs${pspEnabled ? ', with a PSP file at Request received' : ''}.`
            : `${postable.length} prescriptions will be added to this patient's specialty drugs${pspEnabled ? ', each with a PSP file at Request received' : ''}.`}
        </DialogContentText>
        {skipped.length > 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="mark-specialty-skipped">
            {rxList(skipped)}: {NO_DRUG_NAME_REASON} — skipped.
          </Typography>
        ) : null}
        {added.length > 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="mark-specialty-already-added">
            {rxList(added)}: already added — not sent again.
          </Typography>
        ) : null}
        {readOnly && readOnlyMessage ? <Alert severity="info" sx={{ mb: 2 }}>{readOnlyMessage}</Alert> : null}
        {pspEnabled === undefined ? (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="mark-specialty-pms-unknown">
            {PMS_UNKNOWN_MESSAGE}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {error}
          </Alert>
        ) : null}
        <Stack spacing={2}>
          <TextField
            label="Received date"
            type="date"
            size="small"
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
            disabled={disabled}
            title={title}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Received date', max: getPharmacyTodayString() }}
          />
          <TextField
            label="Notes"
            size="small"
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={disabled}
            title={title}
            inputProps={{ 'aria-label': 'Notes' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={disabled || postable.length === 0 || !receivedDate || pspEnabled === undefined}
          title={title}
        >
          {saving ? 'Saving…' : 'Mark as specialty'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkSpecialtyDialog;
